import { useState, useRef, useEffect } from 'react';
import { api } from '../lib/api.js';

// Knowledge → Ask Empire: the KB's Ask assistant inside the Monarch shell.
// Conversation history is held in component state and sent with each question
// (follow-ups keep context); answers arrive as sanitized HTML from the KB.
// Role scoping happens at the KB's data layer via the proxy identity — this
// view has no role logic at all.

// Fallback only — the live chips come from the KB's rotating role-aware
// pool via GET /api/ask/chips (CJ 2026-07-12: the hardcoded copy of the
// original four never rotated).
const FALLBACK_CHIPS = [
  'What were our best sellers this weekend?',
  "Who's working tomorrow at my store?",
  'What tasks should I be working on right now?',
  'What does the Service Standard say about remakes?',
];

export function AskEmpireView({ user }) {
  const headers = { 'x-user-id': String(user.id) };
  const [chips, setChips] = useState(FALLBACK_CHIPS);
  useEffect(() => {
    let live = true;
    api('GET', '/api/ask/chips', null, headers)
      .then((r) => { if (live && Array.isArray(r?.chips) && r.chips.length) setChips(r.chips); })
      .catch(() => {}); // KB down → keep the static fallback
    return () => { live = false; };
  }, []);
  const [messages, setMessages] = useState([
    { who: 'bot', html: "Hi! I answer from Empire's documents and sales data. What you can see depends on your role, and I'll always tell you honestly when something is out of scope." },
  ]);
  const [history, setHistory] = useState([]); // {question, answer_md}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  async function send(text) {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { who: 'user', text: q }, { who: 'bot', html: '<em>thinking…</em>', pending: true }]);
    try {
      const data = await api('POST', '/api/ask', { question: q, history: history.slice(-6) }, headers);
      setHistory((h) => [...h, { question: q, answer_md: data.answer_md || '' }]);
      setMessages((m) => [
        ...m.slice(0, -1),
        { who: 'bot', html: data.answer_html, cited: data.cited_slugs || [], qid: data.question_id },
      ]);
    } catch (e) {
      setMessages((m) => [...m.slice(0, -1), { who: 'bot', html: '', text: e.message || 'Something went wrong — try again.' }]);
    } finally { setBusy(false); }
  }

  async function rate(qid, rating, idx) {
    try { await api('POST', `/api/ask/${qid}/rate`, { rating }, headers); } catch (_) { /* best effort */ }
    setMessages((m) => m.map((msg, i) => (i === idx ? { ...msg, rated: true } : msg)));
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', height: '68vh', minHeight: 420, padding: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((m, i) => (
          <div key={i} style={{
            maxWidth: '85%', padding: '9px 13px', borderRadius: 13, fontSize: 14,
            alignSelf: m.who === 'user' ? 'flex-end' : 'flex-start',
            background: m.who === 'user' ? 'var(--gold, #c4934d)' : 'var(--cream-soft, #efeae0)',
            color: m.who === 'user' ? '#fff' : 'var(--text-primary)',
          }}>
            {m.who === 'user' ? m.text : (
              <div>
                {m.html ? <div className="kb-doc-body" dangerouslySetInnerHTML={{ __html: m.html }} /> : m.text}
                {m.cited?.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 10, color: 'var(--gold-text, #8a5a1a)', fontFamily: 'monospace' }}>
                    Sources: {m.cited.join(' · ')}
                  </div>
                )}
                {m.qid && !m.rated && (
                  <div style={{ marginTop: 7, display: 'flex', gap: 6 }}>
                    <button className="btn" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => rate(m.qid, 1, i)}>Solved it</button>
                    <button className="btn" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => rate(m.qid, 0, i)}>Didn’t solve it</button>
                  </div>
                )}
                {m.rated && <div style={{ marginTop: 6, fontSize: 10, color: 'var(--text-secondary)' }}>Thanks — logged.</div>}
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 12px 10px' }}>
        {chips.map((c) => (
          <button key={c} onClick={() => send(c)} disabled={busy}
            style={{ fontSize: 11.5, padding: '5px 11px', borderRadius: 13, border: '1px solid var(--rule, #e6e1d6)',
              background: 'var(--paper, #fff)', color: 'var(--text-secondary)', cursor: 'pointer' }}>{c}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid var(--rule, #e6e1d6)' }}>
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder="Ask about recipes, standards, or sales patterns…"
          style={{ flex: 1, padding: '10px 12px', fontSize: 14, borderRadius: 9, border: '1px solid var(--rule, #e6e1d6)' }} />
        <button className="btn btn-primary" onClick={() => send()} disabled={busy}>Ask</button>
      </div>
      <div style={{ fontSize: 9.5, color: 'var(--text-secondary)', fontFamily: 'monospace', padding: '0 14px 10px' }}>
        Questions are logged anonymously (role + location) to improve our docs. Individual history is never shown to anyone.
      </div>
    </div>
  );
}
